/**
 * Reload once a new build is deployed, so nobody keeps running a fixed bug.
 *
 * On 2026-10-01 a fix stopping a week being submitted with unsaved grid hours
 * went live, and every tab opened earlier kept the old code - and the bug - until
 * somebody thought to refresh. index.html is never cached (see nginx.conf) and
 * names the hashed bundle, so a different name there means a different build.
 *
 * Never while the grid holds unsaved typing: losing typed hours to a reload is
 * the very thing the fix was for. It waits and tries again on the next check.
 */
let unsaved = false;
export const setUnsavedWork = (value: boolean) => {
  unsaved = value;
};

// The entry SCRIPT, not any /assets/ name: Vite appends preload links for lazy
// chunks to the live page, and those are not in the file served.
const bundle = (html: string) => html.match(/<script[^>]+src="(\/assets\/[^"]+\.js)"/)?.[1] ?? null;

export function reloadOnDeploy(everyMs = 5 * 60_000) {
  const running = document.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/"]')
    ?.getAttribute('src') ?? null;
  // The dev server serves /src/main.tsx, not a hashed bundle: nothing to compare.
  if (!running) return;

  const check = async () => {
    if (unsaved || document.visibilityState !== 'visible') return;
    try {
      const response = await fetch('/', { cache: 'no-store' });
      const deployed = response.ok ? bundle(await response.text()) : null;
      if (deployed && deployed !== running && !unsaved) window.location.reload();
    } catch {
      // Offline, or mid-deploy. The next check will see it.
    }
  };
  setInterval(check, everyMs);
  // Coming back to a tab left open overnight is when it is most likely stale.
  document.addEventListener('visibilitychange', () => void check());
}
